import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Role } from '../../../src/admin/types';
import { exchangeCode, logoutRedirect, startLogin } from './cognito';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthUser {
  email: string;
  role: Role | null;
  displayName: string | null;
  sub: string;
}

interface AuthState {
  status: AuthStatus;
  idToken: string | null;
  user: AuthUser | null;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type JwtPayload = Record<string, unknown>;

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadSegment = parts[1];
    if (!payloadSegment) return null;
    const padded = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function readString(claims: JwtPayload, key: string): string | null {
  const v = claims[key];
  return typeof v === 'string' ? v : null;
}

function userFromToken(token: string): AuthUser | null {
  const claims = decodeJwt(token);
  if (!claims) return null;
  const sub = readString(claims, 'sub');
  const email = readString(claims, 'email');
  if (!sub || !email) return null;
  const rawRole = readString(claims, 'custom:role');
  const role: Role | null =
    rawRole === 'admin' || rawRole === 'editor' || rawRole === 'viewer' ? rawRole : null;
  return {
    sub,
    email,
    role,
    displayName: readString(claims, 'name'),
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    idToken: null,
    user: null,
  });

  // React Strict Mode runs effects twice in dev. Token-exchange isn't idempotent
  // — the auth code is single-use, so the second invocation would fail and clobber
  // the success path. Gate with a ref so we only attempt the exchange once per mount.
  const exchangeStarted = useRef(false);

  // On mount: complete OAuth callback (?code=...) if present; otherwise mark anonymous.
  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const stateParam = params.get('state');
      if (code && stateParam && window.location.pathname === '/auth/callback') {
        try {
          const tokens = await exchangeCode(code, stateParam);
          const user = userFromToken(tokens.idToken);
          setState({
            status: user ? 'authenticated' : 'anonymous',
            idToken: user ? tokens.idToken : null,
            user,
          });
        } catch {
          setState({ status: 'anonymous', idToken: null, user: null });
        }
        return;
      }
      setState({ status: 'anonymous', idToken: null, user: null });
    })();
  }, []);

  const login = useCallback(async () => {
    await startLogin();
  }, []);

  const logout = useCallback(() => {
    setState({ status: 'anonymous', idToken: null, user: null });
    logoutRedirect();
  }, []);

  const value: AuthContextValue = { ...state, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
